import * as React from "react";
import { useMemo } from "react";
import styled from "styled-components";
import { Formik, Form } from "formik";
import { useTranslation } from "react-i18next";
import { Col, Row, RowContainer } from "components/lookup-tables/layout-componets";
import { FormSubmit } from "components/common";
import { useCreateCache, useUpdateCache } from "components/lookup-tables/hooks/useLookupTablesAPI";
import useScopePermissions from "hooks/useScopePermissions";
import usePluginEntities from "hooks/usePluginEntities";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import CacheFormFields from "./CacheFormFields";
const INIT_CACHE = {
  id: void 0,
  title: "",
  description: "",
  name: "",
  config: {}
};
const FlexForm = styled(Form)`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding-bottom: ${({ theme }) => theme.spacings.md};
`;
const Title = ({ title, typeName, create }) => {
  const TagName = create ? "h3" : "h2";
  return /* @__PURE__ */ React.createElement(TagName, { style: { marginBottom: "12px", width: "100%" } }, title, " ", /* @__PURE__ */ React.createElement("small", null, "(", typeName, ")"));
};
function CacheForm({ type, saved, title, onCancel, create = false, cache = INIT_CACHE }) {
  const { t } = useTranslation("lookup_tables");
  const sendTelemetry = useSendTelemetry("lut_cache");
  const { loadingScopePermissions, scopePermissions } = useScopePermissions(cache);
  const { createCache, creatingCache } = useCreateCache();
  const { updateCache, updatingCache } = useUpdateCache();
  const cachePlugins = usePluginEntities("lookupTableCaches");
  const plugin = useMemo(() => cachePlugins.find((p) => p.type === type), [cachePlugins, type]);
  const DocComponent = useMemo(() => plugin == null ? void 0 : plugin.documentationComponent, [plugin]);
  const pluginDisplayName = useMemo(() => (plugin == null ? void 0 : plugin.displayName) || type, [plugin, type]);
  const handleSubmit = async (values) => {
    const promise = create ? createCache(values) : updateCache(values);
    return promise.then((response) => {
      var _a;
      sendTelemetry(TELEMETRY_EVENT_TYPE.LUT[create ? "CACHE_CREATED" : "CACHE_UPDATED"], {
        app_pathname: "lut",
        event_details: {
          type: (_a = values == null ? void 0 : values.config) == null ? void 0 : _a.type
        }
      });
      saved(response);
    });
  };
  const canModify = useMemo(
    () => create || !loadingScopePermissions && (scopePermissions == null ? void 0 : scopePermissions.is_mutable),
    [create, loadingScopePermissions, scopePermissions == null ? void 0 : scopePermissions.is_mutable]
  );
  return /* @__PURE__ */ React.createElement(RowContainer, { style: { flexGrow: 1 }, $withDocs: !!DocComponent }, /* @__PURE__ */ React.createElement(Col, { style: { flexGrow: 1, height: "100%" } }, /* @__PURE__ */ React.createElement(Title, { title, typeName: pluginDisplayName, create }), /* @__PURE__ */ React.createElement(Formik, { initialValues: cache, onSubmit: handleSubmit, validateOnBlur: false, enableReinitialize: true }, ({ isSubmitting, isValid }) => /* @__PURE__ */ React.createElement(FlexForm, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement(Row, { $gap: "xl", style: { flexGrow: 1 } }, /* @__PURE__ */ React.createElement("div", { style: { width: DocComponent ? "60%" : "100%" } }, /* @__PURE__ */ React.createElement(CacheFormFields, null)), DocComponent && /* @__PURE__ */ React.createElement("div", { style: { width: "40%", flexGrow: 0 } }, /* @__PURE__ */ React.createElement(DocComponent, { cacheId: cache == null ? void 0 : cache.id }))), canModify && /* @__PURE__ */ React.createElement(Row, { $align: "center", $justify: "flex-end" }, /* @__PURE__ */ React.createElement(
    FormSubmit,
    {
      submitButtonText: create ? t("cache_form.create_submit") : t("cache_form.update_submit"),
      submitLoadingText: create ? t("cache_form.creating_submit") : t("cache_form.updating_submit"),
      isSubmitting: isSubmitting || creatingCache || updatingCache,
      disabledSubmit: isSubmitting || creatingCache || updatingCache || !isValid,
      isAsyncSubmit: true,
      onCancel
    }
  ))))));
}
export default CacheForm;
